import queryString, { stringify } from 'query-string';
import getCheckoutSlug from '../services/getCheckoutSlug';
import getSellerSlug from '../services/getSellerSlug';
import postMessageToFrame from '../utils/postMessageToFrame';
import getRedirectUrl from '../lib/getRedirectUrl';
import getRedirectQuery from '../lib/getRedirectQuery';
import useCheckoutState from './useCheckoutState';
import getCheckoutPath from '../lib/getCheckoutPath';
import useCheckoutRouter from './useCheckoutRouter';
import { checkoutTypes } from '../enums/checkout-types';

const useRedirectAfterPayment = () => {
  const {
    checkout,
    product,
    checkoutType,
    query: currentQuery,
    iframeId,
    isTesting,
    isPreview,
  } = useCheckoutState();

  const router = useCheckoutRouter();

  const initialCheckout = currentQuery.initialCheckout || getCheckoutSlug(checkout);

  async function redirectUpsellURL(payment, redirect) {
    const redirectUrl = getRedirectUrl({
      searchParams: currentQuery,
      iframeId,
      url: redirect.url,
      path: redirect.path,
      query: redirect.query,
      payment,
      product,
      checkout,
    });

    switch (checkoutType) {
      case checkoutTypes.EMBED: {
        window.top.location.href = redirectUrl;
        break;
      }
      case checkoutTypes.POPUP:
      case checkoutTypes.CHECKOUT:
      case checkoutTypes.IFRAME: {
        if (checkoutType === checkoutTypes.CHECKOUT) {
          window.location.href = redirectUrl;
        } else {
          window.top.location.href = redirectUrl;
        }
        break;
      }
    }
  }

  async function redirectUpsellCheckout(payment, upsellCheckoutSlug) {
    const query = getRedirectQuery({
      payment,
      searchParams: currentQuery,
      iframeId,
    });

    return handleRedirectCheckout(upsellCheckoutSlug, query);
  }

  function handleRedirectCheckout(checkoutSlug, query = {}) {
    const sellerSlug = getSellerSlug(checkout.seller);

    // in preview, we don't actually render the pop-up, so pop-up redirect logic doesn't work
    // so we just redirect to the checkout instead
    if (isPreview && isTesting && checkoutType === checkoutTypes.POPUP) {
      const redirectUrl = queryString.stringifyUrl(
        {
          url: getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.POPUP),
          query,
        },
        { encode: false },
      );
      router.push(redirectUrl);
      return;
    }

    switch (checkoutType) {
      case checkoutTypes.EMBED: {
        const redirectUrl = queryString.stringifyUrl(
          {
            url: getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.EMBED),
            query: {
              ...query,
              iframeId,
              initialCheckout: initialCheckout,
            },
          },
          { encode: false },
        );

        router.push(redirectUrl);

        postMessageToFrame({
          seller: sellerSlug,
          checkout: checkoutSlug,
          iframeId: iframeId,
          query: currentQuery,
          type: 'checkoutpage.scrollIntoView',
          payload: {},
        });
        break;
      }
      case checkoutTypes.POPUP: {
        const mergedQuery = {
          ...currentQuery,
          ...query,
        };

        const redirectUrl = queryString.stringifyUrl(
          {
            url: getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.POPUP),
            query: mergedQuery,
          },
          { encode: false },
        );
        router.push(redirectUrl);
        break;
      }
      case checkoutTypes.CHECKOUT:
      case checkoutTypes.IFRAME:
      default: {
        const redirectUrl = queryString.stringifyUrl(
          {
            url: getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT),
            query,
          },
          { encode: false },
        );
        router.push(redirectUrl);
        break;
      }
    }
  }

  async function redirectConfirmation(paymentId, checkoutSlug) {
    const sellerSlug = getSellerSlug(checkout.seller);

    switch (checkoutType) {
      case checkoutTypes.POPUP: {
        const redirectUrl = queryString.stringifyUrl(
          {
            url: `${getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.POPUP)}/confirmation/${paymentId}`,
            query: {
              ...currentQuery,
              iframeId: iframeId,
            },
          },
          { encode: false },
        );
        router.push(redirectUrl);
        break;
      }
      case checkoutTypes.EMBED: {
        const redirectUrl = queryString.stringifyUrl(
          {
            url: `${getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.EMBED)}/confirmation/${paymentId}`,
            query: {
              ...currentQuery,
              iframeId: iframeId,
            },
          },
          { encode: false },
        );

        router.push(redirectUrl);

        postMessageToFrame({
          seller: sellerSlug,
          checkout: checkoutSlug,
          iframeId: iframeId,
          query: currentQuery,
          type: 'checkoutpage.scrollIntoView',
          payload: {},
        });
        break;
      }
      case checkoutTypes.CHECKOUT:
      case checkoutTypes.IFRAME:
      default: {
        const redirectUrl = queryString.stringifyUrl(
          {
            url: `${getCheckoutPath(sellerSlug, checkoutSlug, checkoutTypes.CHECKOUT)}/confirmation/${paymentId}`,
            query: {
              ...currentQuery,
              iframeId: iframeId,
            },
          },
          { encode: false },
        );
        router.push(redirectUrl);
        break;
      }
    }
  }

  async function redirectCheckout(payment, checkoutSlug) {
    const query = getRedirectQuery({
      payment,
      searchParams: currentQuery,
    });

    handleRedirectCheckout(checkoutSlug, query);
  }

  async function redirectURL(payment, { url, path, query }, redirectUrlInsideEmbed) {
    switch (checkoutType) {
      case checkoutTypes.IFRAME:
      case checkoutTypes.EMBED: {
        const redirectUrl = getRedirectUrl({
          searchParams: currentQuery,
          url,
          path,
          query,
          payment,
          product,
        });

        if (redirectUrlInsideEmbed) {
          const url = queryString.stringifyUrl(
            {
              url: redirectUrl,
              query: {
                initialCheckout,
                iframeId,
              },
            },
            { encode: false },
          );

          window.location.href = url;
        } else {
          window.top.location.href = redirectUrl;
        }
        break;
      }
      case checkoutTypes.CHECKOUT: {
        const redirectUrl = getRedirectUrl({
          searchParams: currentQuery,
          url,
          path,
          query,
          payment,
          product,
        });

        window.location.href = redirectUrl;
        break;
      }
      case checkoutTypes.POPUP: {
        const redirectUrl = getRedirectUrl({
          searchParams: currentQuery,
          url,
          path,
          query,
          payment,
          product,
        });

        window.top.location.href = redirectUrl;
        break;
      }
    }
  }

  return {
    redirectUpsellURL,
    redirectUpsellCheckout,
    redirectCheckout,
    redirectConfirmation,
    redirectURL,
  };
};

export default useRedirectAfterPayment;
